import { setSpanDistinctId } from "./otel";
// oxlint-disable-next-line import/no-named-as-default -- posthog-js's default export is the singleton entrypoint the adapter (and its test mock) depend on; the named `posthog` is the same object but default is the documented entrypoint.
import posthog from "posthog-js";
import type { AnalyticsClient, AnalyticsConfig } from "./types";

/**
 * PostHog adapter — the ONLY module in the workspace allowed to import posthog-js.
 */
export class PostHogAnalytics implements AnalyticsClient {
  private ready = false;

  init(config: AnalyticsConfig): void {
    if (this.ready || !config.apiKey) return;
    posthog.init(config.apiKey, {
      // api_host = VITE_POSTHOG_HOST: in prod point it at your first-party PostHog reverse-proxy
      // subdomain (e.g. https://e.yourdomain.com) to defeat ad blockers; dev uses eu.i.posthog.com.
      api_host: config.host ?? "https://eu.i.posthog.com",
      // ui_host = PostHog's real UI domain (region-specific) — required so the toolbar works behind
      // the proxy. EU Cloud => eu.posthog.com (use us.posthog.com for US Cloud).
      ui_host: config.uiHost ?? "https://eu.posthog.com",
      // PostHog's pinned modern defaults bundle (per the official guides). Enables history-aware
      // SPA/MPA pageview capture, autocapture, pageleave, etc. — so neither app tracks pageviews
      // manually. person_profiles kept explicit for cost/privacy control.
      defaults: "2026-01-30",
      person_profiles: "identified_only",
      // Error tracking (ADR-0015): autocapture window errors + unhandled rejections; caught
      // errors are reported explicitly via captureException (error boundaries).
      capture_exceptions: true,
    });
    this.ready = true;
  }

  capture(event: string, properties?: Record<string, unknown>): void {
    if (!this.ready) return;
    posthog.capture(event, properties);
  }

  identify(distinctId: string, traits?: Record<string, unknown>): void {
    if (!this.ready) return;
    posthog.identify(distinctId, traits);
    // Same person key on every span (ADR-0035): traces pivot from the PostHog profile.
    setSpanDistinctId(distinctId);
  }

  captureException(error: unknown, properties?: Record<string, unknown>): void {
    if (!this.ready) return;
    posthog.captureException(error, properties);
  }

  reset(): void {
    if (!this.ready) return;
    posthog.reset();
  }

  optOut(): void {
    if (!this.ready) return;
    // Stops all capture for this browser and clears the person/session (consent withdrawn mid-session).
    posthog.opt_out_capturing();
  }

  isFeatureEnabled(flag: string): boolean {
    if (!this.ready) return false;
    return posthog.isFeatureEnabled(flag) ?? false;
  }

  getSessionId(): string | undefined {
    if (!this.ready) return undefined;
    return posthog.get_session_id() ?? undefined;
  }

  getDistinctId(): string | undefined {
    if (!this.ready) return undefined;
    return posthog.get_distinct_id() ?? undefined;
  }
}

export const analytics: AnalyticsClient = new PostHogAnalytics();
